import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import Link from 'ui/atoms/link';
import { TruncatedTooltip } from 'src/ui/atoms/truncated-tooltip';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { PaginationProps } from 'ui/molecules/pagination';
import {
  DocumentsApi,
  AdminDocument,
  AdminDocumentsListOrderingEnum,
  AdminDocumentsValuesRetrieveFieldEnum,
} from 'src/api';
import useApiCall from 'src/hooks/use-api-call';

export interface DocumentsTableProps {
  investmentId: string;
  title?: ReactNode;
  loading?: boolean;
  paginationProps?: PaginationProps;
  defaultLimit?: number;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  hasPermission?: boolean;
  skipFilterUrlSync?: boolean;
}

const DocumentsTable: FunctionComponent<DocumentsTableProps> = WithDataRecord<DocumentsApi, AdminDocument>(
  DocumentsApi,
)(
  ({ data, ordering, onOrderBy, title, loading, paginationProps, hasPermission, investmentId }) => {
    const { makeAuthenticatedApi } = useApiCall();
    const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

    return (
      <DataRecordTable
        data={data}
        ordering={ordering}
        onOrderBy={onOrderBy}
        title={title}
        loading={loading}
        paginationProps={paginationProps}
        hasPermission={hasPermission}
        cells={[
          {
            title: <Translate name="documentTable.document" />,
            render: (document) => <TruncatedTooltip>{document.name}</TruncatedTooltip>,
            orderingFieldName: AdminDocumentsListOrderingEnum.NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'name',
            searchFieldName: AdminDocumentsValuesRetrieveFieldEnum.NAME,
            getFilterData: (params: any) =>
              documentsApi.adminDocumentsValuesRetrieve({ ...params, investment: investmentId }),
          },
          {
            title: <Translate name="documentTable.actions" />,
            width: 100,
            render: (document) =>
              document.file ? (
                <Link
                  href={document.file}
                  target="_blank"
                  icon="download"
                  download={true}
                  iconColor="primary"
                  iconSize="large"
                />
              ) : null,
          },
        ]}
      />
    );
  },
  (api, props, offset, limit, ordering) => {
    const { investmentId, ...remainingProps } = props;

    return api.adminDocumentsList({
      offset,
      limit,
      ordering: ordering as AdminDocumentsListOrderingEnum,
      investment: investmentId,
      ...remainingProps,
    });
  },
);

export default DocumentsTable;
